import {BaseCommand} from './base.command';
import {ElementSnapshot} from '../interfaces/command.interface';
import {CytoscapeService} from '../../cy-services/cytoscape.service';
import {NodeService} from '../../../api/services/node.service';
import {firstValueFrom} from 'rxjs';
import {UndoLabelMetadata} from '../undo-label.utils';

/**
 * PasteCommand handles paste operations.
 * The command is created AFTER paste completes, capturing the created elements.
 * Undo deletes the pasted elements.
 */
export class PasteCommand extends BaseCommand<ElementSnapshot> {
  constructor(
    private cyService: CytoscapeService,
    private nodeService: NodeService,
    pastedElements: ElementSnapshot
  ) {
    const labelMetadata: UndoLabelMetadata = {
      object: 'Clipboard',
      verb: 'Paste'
    };

    super(
      'paste',
      'Clipboard Paste',
      {
        before: {nodes: [], links: []},
        after: pastedElements
      },
      labelMetadata
    );
  }

  async execute(): Promise<void> {
    // This is called on redo - we need to re-create the pasted elements
    // However, since paste creates new IDs, we can't simply re-execute
    // Instead, we restore the soft-deleted elements

    const cy = this.cyService.cy;
    if (!cy) return;

    const {nodes, links} = this.snapshot.after;

    // Collect all IDs
    const allIds = [
      ...nodes.map(n => n.id),
      ...links.map(l => l.id)
    ];

    // Restore via backend API
    await firstValueFrom(this.nodeService.restoreWithChildren(allIds));

    // Re-add to Cytoscape
    cy.batch(() => {
      // Add nodes
      for (const node of nodes) {
        if (cy.$(`#${node.id}`).length > 0) continue;

        cy.add({
          group: 'nodes',
          data: {...node.data} as any,
          position: {...node.position},
          classes: node.classes.join(' ')
        });

        if (node.parentId) {
          cy.$(`#${node.id}`).move({parent: node.parentId});
        }
      }

      // Add links
      for (const link of links) {
        if (cy.$(`#${link.id}`).length > 0) continue;

        cy.add({
          group: 'edges',
          data: {
            ...link.data,
            source: link.sourceId,
            target: link.targetId
          }
        });
      }
    });

    // Reinitialize edge editing handles for restored links with control/bend points
    this.reinitializeEdgeHandles(cy, links);

    cy.style().update();
  }

  /**
   * Reinitialize edge editing handles for restored links
   */
  private reinitializeEdgeHandles(cy: any, links: any[]): void {
    if (!(cy as any).edgeEditing) return;
    const edgeEditingInstance = (cy as any).edgeEditing('get');
    if (!edgeEditingInstance) return;

    for (const link of links) {
      const edge = cy.$(`#${link.id}`);
      if (edge.length === 0) continue;

      const linkType = link.data.type || 'STRAIGHT';
      const controlPoints = link.data.controlPointPositions;
      const bendPoints = link.data.bendPointPositions;

      // Update classes based on link type
      edge.removeClass('edgebendediting-hasbendpoints');
      edge.removeClass('edgecontrolediting-hascontrolpoints');

      if (linkType === 'BEZIER') {
        edge.addClass('ignore_control_points');
      } else {
        edge.removeClass('ignore_control_points');
      }

      // Add appropriate class if we have anchor data
      if (linkType === 'UNBUNDLED_BEZIER' && controlPoints?.length > 0) {
        edge.addClass('edgecontrolediting-hascontrolpoints');
      } else if (linkType === 'STRAIGHT' && bendPoints?.length > 0) {
        edge.addClass('edgebendediting-hasbendpoints');
      }

      // Reinitialize anchor points visualization
      const edgeEditingType = linkType === 'BEZIER' ? 'none' : linkType === 'STRAIGHT' ? 'bend' : 'control';
      edgeEditingInstance.initAnchorPoints([edge], edgeEditingType);
    }
  }

  async undo(): Promise<void> {
    const cy = this.cyService.cy;
    if (!cy) return;

    const {nodes, links} = this.snapshot.after;

    // Remove from Cytoscape
    cy.batch(() => {
      links.forEach(link => {
        cy.$(`#${link.id}`).remove();
      });
      nodes.forEach(node => {
        cy.$(`#${node.id}`).remove();
      });
    });

    // Soft-delete on backend
    const deletePromises: Promise<unknown>[] = [];

    for (const node of nodes) {
      deletePromises.push(firstValueFrom(this.nodeService.deleteNode(node.id)));
    }

    for (const link of links) {
      deletePromises.push(firstValueFrom(this.nodeService.deleteLink(link.id)));
    }

    await Promise.all(deletePromises);
  }
}
